import React, {useCallback, useEffect, useMemo, useState} from "react";
import Resources from "../../../../data/services/resources";
import {createResource, getResource, updateResource} from "../../../../data/actions/resource";
import {getUser} from "../../../../util/util";
import {DEFAULT_METADATA_SELECT_SEARCH_QUERY} from "../../../../util/util-constants";
import {Field, FieldsManager} from "../../../../data/services/fields";
import {fillFieldsFromData} from "../../../../common/util/util-fields";
import {classNames, getProp} from "../../../../common/util/util-helpers";
import PageTabWithFooter from "../../../../common/components/layout/layout-components/page/page-tab-with-footer";
import ModalConfirm from "../../../../common/components/modal/modal-confirm";
import {
    STATUSES,
    applyBulkStatus,
    buildAnswersPayload,
    computeInspectionResult,
    emptyBrakeRow,
    emptyTireRow,
    getBrakePreset,
    getTirePreset,
    getVisibleSections,
    hydrateBrakeRows,
    hydrateTireRows,
    toggleItemStatus
} from "./dot-inspection-config";
import DotInspectionHeaderSection from "./dot-inspection-header-section";
import DotInspectionSectionCard from "./dot-inspection-section-card";
import DotInspectionResultSection from "./dot-inspection-result-section";
import {useSelector} from "react-redux";
import moment from "moment";
import LocalStorage from "../../../../util/localStorage";
import {LoaderLarge} from "../../../../common/components/loader";
import Button from "../../../../common/components/button";
import {download} from "../../../../data/actions/download";
import {currentDate} from "../../../../common/util/util-dates";

const DEFAULT_VEHICLE_TYPE = 'semi';

function validateVinOrLicensePlate(fieldsToValidate) {
    const hasVin = !!fieldsToValidate.VIN.value?.toString().trim();
    const hasLicensePlate = !!fieldsToValidate.LicensePlate.value?.toString().trim();
    const errorMessage = (hasVin || hasLicensePlate) ? null : ['fields.errors.empty'];

    return Object.assign({}, fieldsToValidate, {
        VIN: Object.assign({}, fieldsToValidate.VIN, {errorMessage}),
        LicensePlate: Object.assign({}, fieldsToValidate.LicensePlate, {errorMessage})
    });
}

export default function WorkOrdersDotInspectionTab({ID, dispatch, translate, isStateDirty, setIsStateDirty}) {
    /** Constants
     ================================================================= */
    // `saved` is a previously stored inspection -- its non-empty values take
    // precedence over the defaults derived from the Work Order.
    const getFields = (item = {}, saved = {}) => {
        const WoRepairShopON = getProp(LocalStorage.get('lookup'), 'FeatureFlags.WORepairShopON')
        if (WoRepairShopON) {
            item.Carrier = item.ChargeOrganization;
            item.CarrierID = item.ChargeOrganizationID;
        } else {
            item.CarrierID = ""
            item.Carrier = ""
        }
        item = Object.assign({}, item);
        item.LicensePlate = item?.TruckInfo?.TruckLicenceNumber;
        item.VIN = item?.TruckInfo?.VIN;
        item.Year = item?.TruckInfo?.Year;
        item.Make = item?.TruckInfo?.Manufacturer;
        item.Odometer = item?.TruckInfo?.Mileage;
        item.UnitNumber = item?.Truck ? item?.Truck : item?.Trailer;

        Object.keys(saved).forEach((key) => {
            if (saved[key] !== null && saved[key] !== undefined && saved[key] !== '') {
                item[key] = saved[key];
            }
        });

        const fieldTemplates = {
            ReportNumber: new Field('ReportNumber', '', ['empty'], false, 'text', {addContainerClass: "col-span-6 sm:col-span-3 lg:col-span-2"}),
            InspectionDate: new Field('InspectionDate', moment().format('YYYY-MM-DD'), ['empty'], false, 'date', {addContainerClass: "col-span-6 sm:col-span-3 lg:col-span-2"}),
            CarrierID: new Field('CarrierID', '', ['empty'], false, 'select-search', {addContainerClass: "col-span-6 sm:col-span-3 lg:col-span-2"}),
            USDOTNumber: new Field('USDOTNumber', '', ['empty'],true, 'text', {addContainerClass: "col-span-6 sm:col-span-3 lg:col-span-2"}),
            UnitNumber: new Field('UnitNumber', '', [], true, 'text', {addContainerClass: "col-span-6 sm:col-span-3 lg:col-span-2"}),
            VIN: new Field('VIN', '', ['empty'], true, 'text', {addContainerClass: "col-span-6 sm:col-span-3 lg:col-span-2", note: translate('text.dot_vin_or_plate_required')}),
            LicensePlate: new Field('LicensePlate', '', ['empty'], true, 'text', {addContainerClass: "col-span-6 sm:col-span-3 lg:col-span-2", note: translate('text.dot_vin_or_plate_required')}),
            Make: new Field('Make', '', [], true, 'text', {addContainerClass: "col-span-6 sm:col-span-3 lg:col-span-2"}),
            Year: new Field('Year', '', ['integer_or_empty'], true, 'text', {addContainerClass: "col-span-6 sm:col-span-3 lg:col-span-2"}),
            Odometer: new Field('Odometer', '', ['integer_or_empty'], true, 'text', {addContainerClass: "col-span-6 sm:col-span-3 lg:col-span-2"}),
            InspectionAgency: new Field('InspectionAgency', '', [], false, 'text', {addContainerClass: "col-span-6"}),

            Notes: new Field('Notes', '', [], false, 'textarea', {addContainerClass: "col-span-full"}),
            AssignedTechnicianID: new Field('AssignedTechnicianID', '', [], false, 'select-search', {addContainerClass: "col-span-6 sm:col-span-3"}, {isClearable: true, menuPlacement: "top"}),
            InspectorID: new Field('InspectorID', '', [], false, 'text', {addContainerClass: "col-span-6 sm:col-span-3"})
        };

        return fillFieldsFromData(fieldTemplates, item);
    };

    const getSelects = () => ({
        AssignedTechnicianID: {
            api: 'api/' + Resources.ContactsQuick,
            query: Object.assign({}, DEFAULT_METADATA_SELECT_SEARCH_QUERY(), {IsTechnician: 1}),
            searchMap: (it) => ({label: it.FirstName + ' ' + it.LastName, value: it.ContactID})
        },
        CarrierID: {
            api: 'api/' + Resources.OrganizationsQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({
                label: item.LegalName,
                value: item.OrganizationID
            })
        }
    });

    /** State
     ================================================================= */
    const thirdResource = useSelector((state) => state.thirdResource)
    const woData = getProp(thirdResource, 'data', [])
    const resource = useSelector((state) => state.resource)
    const isLoading = getProp(resource, "isLoading", true);
    const resourceData = getProp(resource, 'data', {})
    // state.resource is shared between tabs, so only treat it as ours when it's
    // an inspection belonging to this Work Order.
    const savedInspection = (resourceData?.WorkOrderDotInspectionID && Number(resourceData.WorkOrderID) === Number(ID))
        ? resourceData
        : null;
    const [fields, setFields] = useState(getFields(woData));
    const [vehicleType, setVehicleType] = useState(DEFAULT_VEHICLE_TYPE);
    const [answers, setAnswers] = useState({});
    const [tireRows, setTireRows] = useState(() => getTirePreset(DEFAULT_VEHICLE_TYPE));
    const [brakeRows, setBrakeRows] = useState(() => getBrakePreset(DEFAULT_VEHICLE_TYPE));
    const [confirmDialogData, setConfirmDialogData] = useState({});

    const visibleSections = useMemo(() => getVisibleSections(vehicleType), [vehicleType]);
    const result = useMemo(() => computeInspectionResult(visibleSections, answers), [visibleSections, answers]);
    const selects = useMemo(() => getSelects(), [translate]);

    /** Data Events
     ================================================================= */
    const inspectionQuery = {WorkOrderID: ID};

    const fetchData = () => {
        dispatch(getResource({
            user: getUser(),
            resource: Resources.WorkOrderDotInspection,
            query: inspectionQuery
        }));
    };

    // Rows keep their PK from the API so the save sends it back and the
    // backend updates them in place instead of recreating them.
    const applyInspection = (inspection) => {
        if (!inspection) {
            setFields(getFields(woData));
            setVehicleType(DEFAULT_VEHICLE_TYPE);
            setAnswers({});
            setTireRows(getTirePreset(DEFAULT_VEHICLE_TYPE));
            setBrakeRows(getBrakePreset(DEFAULT_VEHICLE_TYPE));
            return;
        }

        const {Answers, TireRows, BrakeRows, ...savedFields} = inspection;

        setFields(getFields(woData, savedFields));
        setVehicleType(inspection.VehicleType || DEFAULT_VEHICLE_TYPE);
        setAnswers(Array.isArray(Answers) || !Answers ? {} : Answers);
        setTireRows(hydrateTireRows(TireRows ?? []));
        setBrakeRows(hydrateBrakeRows(BrakeRows ?? []));
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (savedInspection) {
            applyInspection(savedInspection);
            setIsStateDirty(false);
        }
    }, [savedInspection]);

    /** UI Events
     ================================================================= */
    const handleVehicleTypeChange = useCallback((type) => {
        setVehicleType(type);
        setAnswers({});
        setTireRows(getTirePreset(type));
        setBrakeRows(getBrakePreset(type));
        setIsStateDirty(true);
    }, [setIsStateDirty]);

    const handleFieldsChange = useCallback((name, value) => {
        setFields((prev) => FieldsManager.updateField(prev, name, value));
        setIsStateDirty(true);
    }, [setIsStateDirty]);

    const handleItemStatusChange = useCallback((itemId, statusKey) => {
        setAnswers((prev) => toggleItemStatus(prev, itemId, statusKey));
        setIsStateDirty(true);
    }, [setIsStateDirty]);

    const handleSectionSetAll = useCallback((sectionId, statusKey) => {
        setAnswers((prev) => {
            const section = visibleSections.find((it) => it.id === sectionId);

            if (!section) {
                return prev;
            }

            return applyBulkStatus(prev, section.items.map((it) => it.id), statusKey);
        });
        setIsStateDirty(true);
    }, [visibleSections, setIsStateDirty]);

    const handleMarkAll = useCallback((statusKey) => {
        setAnswers((prev) => {
            const itemIds = visibleSections.flatMap((section) => section.items.map((it) => it.id));

            return applyBulkStatus(prev, itemIds, statusKey);
        });
        setIsStateDirty(true);
    }, [visibleSections, setIsStateDirty]);

    const handleTireRowChange = useCallback((index, key, value) => {
        setTireRows((prev) => prev.map((row, i) => i === index ? Object.assign({}, row, {[key]: value}) : row));
        setIsStateDirty(true);
    }, [setIsStateDirty]);

    const handleAddTireRow = useCallback(() => {
        setTireRows((prev) => prev.concat([emptyTireRow()]));
        setIsStateDirty(true);
    }, [setIsStateDirty]);

    const handleRemoveTireRow = useCallback((index) => {
        setTireRows((prev) => prev.filter((row, i) => i !== index));
        setIsStateDirty(true);
    }, [setIsStateDirty]);

    const handleBrakeRowChange = useCallback((index, key, value) => {
        setBrakeRows((prev) => prev.map((row, i) => i === index ? Object.assign({}, row, {[key]: value}) : row));
        setIsStateDirty(true);
    }, [setIsStateDirty]);

    const handleAddBrakeRow = useCallback(() => {
        setBrakeRows((prev) => prev.concat([emptyBrakeRow()]));
        setIsStateDirty(true);
    }, [setIsStateDirty]);

    const handleRemoveBrakeRow = useCallback((index) => {
        setBrakeRows((prev) => prev.filter((row, i) => i !== index));
        setIsStateDirty(true);
    }, [setIsStateDirty]);

    const closeConfirmDialog = () => {
        setConfirmDialogData(Object.assign({}, confirmDialogData, {isOpen: false}));
    };

    const handleConfirmCancelClick = () => {
        applyInspection(savedInspection);
        closeConfirmDialog();
        setIsStateDirty(false);
    };

    const handleCancelClick = () => {
        setConfirmDialogData({
            isOpen: true,
            onClick: () => handleConfirmCancelClick(),
            type: "danger",
            content: `Are you sure you want to discard all changes?`,
            title: translate("Delete unsaved changes"),
            buttonLabel: translate("btn.delete")
        });
    };

    const handleSave = () => {
        // VIN/LicensePlate are excluded from this generic pass -- their
        // validate: ['empty'] only drives the label asterisk, the actual
        // "at least one of the two" rule is enforced by
        // validateVinOrLicensePlate below.
        const fieldsToValidateGenerically = Object.keys(fields).filter((name) => name !== 'VIN' && name !== 'LicensePlate');
        let validatedFields = FieldsManager.validateFields(fields, fieldsToValidateGenerically);
        validatedFields = validateVinOrLicensePlate(validatedFields);

        setFields(validatedFields);

        if (!FieldsManager.checkFieldsForErrors(validatedFields)) {
            return;
        }

        const params = Object.assign(
            FieldsManager.getFieldKeyValuesAndLabels(validatedFields, selects),
            {
                WorkOrderID: ID,
                VehicleType: vehicleType,
                Result: result.result,
                Answers: buildAnswersPayload(visibleSections, answers),
                TireRows: tireRows.map(({_uid, ...row}) => row),
                BrakeRows: brakeRows.map(({_uid, ...row}) => row)
            }
        );

        const saveParams = {
            user: getUser(),
            params: params,
            query: inspectionQuery,
            resource: Resources.WorkOrderDotInspection,
            piggyResource: Resources.WorkOrderDotInspection,
            successMessage: translate('message.dot_inspection_saved'),
            errorMessage: true
        };

        if (savedInspection) {
            params.WorkOrderDotInspectionID = savedInspection.WorkOrderDotInspectionID;
            dispatch(updateResource(saveParams));
        } else {
            dispatch(createResource(saveParams));
        }

        setIsStateDirty(false);
    };

    const handleDownloadPdf = () => {
        dispatch(download({
            user: getUser(),
            resource: Resources.WorkOrderDotInspection,
            query: {
                WorkOrderDotInspectionID: savedInspection.WorkOrderDotInspectionID,
                Pdf: 1,
                name: "dot_inspection_" + ID + "_" + currentDate() + ".pdf"
            },
            errorMessage: true,
            successMessage: translate('message.dot_inspection_pdf_downloaded')
        }));
    };

    /** Component Body
     ================================================================= */
    return (
        <PageTabWithFooter
            isLoading={isLoading}
            buttons={[
                {className: "btn btn-primary", onClick: handleSave, disabled: !isStateDirty, label: translate("btn.save")},
                {className: "btn btn-outline", disabled: !isStateDirty, onClick: handleCancelClick, label: translate("btn.cancel")},
            ]}
        >
            {!!isLoading && (
                <LoaderLarge />
            )}

            {!isLoading && (
                <div className="px-4 sm:px-6 md:px-8 pb-10 space-y-4">
                    <DotInspectionHeaderSection
                        fields={fields}
                        vehicleType={vehicleType}
                        onVehicleTypeChange={handleVehicleTypeChange}
                        onFieldsChange={handleFieldsChange}
                        selects={selects}
                        translate={translate}
                        afterTitle={
                            <Button onClick={handleDownloadPdf} disabled={!savedInspection || isStateDirty}>
                                {translate('btn.download_pdf')}
                            </Button>
                        }
                    />

                    <div className="flex justify-end">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-xs text-tm-gray-800 mr-1">{translate('text.dot_mark_all')}:</span>

                            {STATUSES.map((it) => (
                                <button
                                    key={it.key}
                                    type="button"
                                    onClick={() => handleMarkAll(it.key)}
                                    className="rounded-btn px-2.5 py-1 text-xs font-medium ring-1 ring-inset bg-inverse text-tm-gray-600 ring-tm-gray-300 hover:bg-tm-gray-100"
                                >
                                    {translate(it.labelKey)}
                                </button>
                            ))}

                            <button
                                type="button"
                                onClick={() => handleMarkAll(null)}
                                className="px-2 text-xs font-medium text-tm-gray-800 hover:text-tm-gray-700"
                            >
                                {translate('btn.dot_clear_all')}
                            </button>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <div className="flex flex-wrap items-center gap-3">
                            {STATUSES.map((it) => (
                                <div key={it.key} className="flex items-center gap-1.5 text-xs text-tm-gray-700">
                                    <span className={classNames("w-2.5 h-2.5 rounded-full", it.dotClass)}/>
                                    {translate(it.labelKey)}
                                </div>
                            ))}
                        </div>
                    </div>

                    {visibleSections.map((section) => (
                        <DotInspectionSectionCard
                            key={section.id}
                            section={section}
                            answers={answers}
                            onItemChange={handleItemStatusChange}
                            onSectionSetAll={handleSectionSetAll}
                            tireRows={tireRows}
                            brakeRows={brakeRows}
                            onTireRowChange={handleTireRowChange}
                            onAddTireRow={handleAddTireRow}
                            onRemoveTireRow={handleRemoveTireRow}
                            onBrakeRowChange={handleBrakeRowChange}
                            onAddBrakeRow={handleAddBrakeRow}
                            onRemoveBrakeRow={handleRemoveBrakeRow}
                            translate={translate}
                        />
                    ))}

                    <DotInspectionResultSection
                        fields={fields}
                        result={result}
                        onFieldsChange={handleFieldsChange}
                        selects={selects}
                        translate={translate}
                    />

                    <ModalConfirm
                        show={confirmDialogData?.isOpen ?? false}
                        type={confirmDialogData?.type}
                        text={confirmDialogData?.content}
                        title={confirmDialogData?.title}
                        onConfirm={confirmDialogData?.onClick}
                        closeButtonLabel={confirmDialogData?.closeButtonLabel ?? translate("btn.cancel")}
                        buttonLabel={confirmDialogData?.buttonLabel}
                        translate={translate}
                        onClose={closeConfirmDialog}
                    />
                </div>
            )}
        </PageTabWithFooter>
    );
}
